import { Routes } from '@angular/router';
import { onboardingStepGuard } from '@guards/onboarding-step.guard';
import { embeddedGuard } from '@guards/embedded.guard';
import { personalDataResolver } from './features/onboarding/personal-data/personal-data.resolver';
import { addressDataResolver } from './features/onboarding/address-data/address-data.resolver';
import { activityDataResolver } from './features/onboarding/activity-data/activity-data.resolver';
import { investmentKnowledgeResolver } from './features/onboarding/investment-knowledge/investment-knowledge.resolver';
import { financialSituationResolver } from './features/onboarding/financial-situation/financial-situation.resolver';
import { pepDataResolver } from './features/onboarding/pep-data/pep-data.resolver';
import { suitabilityResultResolver } from './features/onboarding/suitability-result/suitability-result.resolver';
import { publicPortfolioResolver } from '@guards/public-portfolio.resolver';

export const routes: Routes = [
  {
    path: 'onboarding/questions',
    canActivate: [onboardingStepGuard],
    loadComponent: () =>
      import('./features/onboarding/survey/survey.component').then(
        (m) => m.SurveyComponent
      )
  },
  {
    path: 'onboarding/test-result',
    canActivate: [onboardingStepGuard],
    resolve: { initData: suitabilityResultResolver },
    loadComponent: () =>
      import('./features/onboarding/suitability-result/suitability-result.component').then(
        (m) => m.SuitabilityResultComponent
      )
  },
  {
    path: 'onboarding/portfolio-detail',
    canActivate: [onboardingStepGuard],
    loadComponent: () =>
      import('./features/onboarding/portfolio-detail/portfolio-detail.component').then(
        (m) => m.PortfolioDetailComponent
      )
  },
  {
    path: 'onboarding',
    loadComponent: () =>
      import('./features/onboarding/shell/shell.component').then(
        (m) => m.ShellComponent
      ),
    children: [
      {
        path: 'welcome',
        canActivate: [onboardingStepGuard],
        loadComponent: () =>
          import('./features/onboarding/welcome/welcome.component').then(
            (m) => m.WelcomeComponent
          )
      },
      {
        path: 'account-owner',
        canActivate: [onboardingStepGuard],
        loadComponent: () =>
          import('./features/onboarding/account-owner/account-owner.component').then(
            (m) => m.AccountOwnerComponent
          )
      },
      {
        path: 'contact-data',
        canActivate: [onboardingStepGuard],
        loadComponent: () =>
          import('./features/onboarding/contact-data/contact-data.component').then(
            (m) => m.ContactDataComponent
          )
      },
      {
        path: 'video-identification',
        canActivate: [onboardingStepGuard],
        loadComponent: () =>
          import('./features/onboarding/video-identification/video-identification.component').then(
            (m) => m.VideoIdentificationComponent
          )
      },
      {
        path: 'personal-data',
        canActivate: [onboardingStepGuard],
        resolve: { initData: personalDataResolver },
        loadComponent: () =>
          import('./features/onboarding/personal-data/personal-data.component').then(
            (m) => m.PersonalDataComponent
          )
      },
      {
        path: 'address-data',
        canActivate: [onboardingStepGuard],
        resolve: { initData: addressDataResolver },
        loadComponent: () =>
          import('./features/onboarding/address-data/address-data.component').then(
            (m) => m.AddressDataComponent
          )
      },
      {
        path: 'activity-data',
        canActivate: [onboardingStepGuard],
        resolve: { initData: activityDataResolver },
        loadComponent: () =>
          import('./features/onboarding/activity-data/activity-data.component').then(
            (m) => m.ActivityDataComponent
          )
      },
      {
        path: 'investment-knowledge',
        canActivate: [onboardingStepGuard],
        resolve: { initData: investmentKnowledgeResolver },
        loadComponent: () =>
          import('./features/onboarding/investment-knowledge/investment-knowledge.component').then(
            (m) => m.InvestmentKnowledgeComponent
          )
      },
      {
        path: 'financial-situation',
        canActivate: [onboardingStepGuard],
        resolve: { initData: financialSituationResolver },
        loadComponent: () =>
          import('./features/onboarding/financial-situation/financial-situation.component').then(
            (m) => m.FinancialSituationComponent
          )
      },
      {
        path: 'pep-data',
        canActivate: [onboardingStepGuard],
        resolve: { initData: pepDataResolver },
        loadComponent: () =>
          import('./features/onboarding/pep-data/pep-data.component').then(
            (m) => m.PepDataComponent
          )
      },
      {
        path: 'sign-contract',
        canActivate: [onboardingStepGuard],
        loadComponent: () =>
          import('./features/onboarding/sign-contract/sign-contract.component').then(
            (m) => m.SignContractComponent
          )
      },
      {
        path: 'create-password',
        canActivate: [onboardingStepGuard],
        loadComponent: () =>
          import('./features/onboarding/create-password/create-password.component').then(
            (m) => m.CreatePasswordComponent
          )
      },
      {
        path: 'completed',
        canActivate: [onboardingStepGuard],
        loadComponent: () =>
          import('./features/onboarding/completed/completed.component').then(
            (m) => m.CompletedComponent
          )
      }
    ]
  },
  {
    path: 'onboarding/inactivity',
    loadComponent: () =>
      import('./features/onboarding/inactivity/inactivity.component').then(
        (m) => m.InactivityComponent
      )
  },
  {
    path: 'onboarding/login',
    loadComponent: () =>
      import('./features/onboarding/recovery-login/recovery-login.component').then(
        (m) => m.RecoveryLoginComponent
      )
  },
  {
    path: 'onboarding/go-to-your-app',
    loadComponent: () =>
      import('./features/onboarding/go-to-app/go-to-app.component').then(
        (m) => m.GoToAppComponent
      )
  },
  {
    path: 'embedded/survey',
    canActivate: [embeddedGuard],
    loadComponent: () =>
      import('./features/onboarding/survey/survey.component').then(
        (m) => m.SurveyComponent
      )
  },
  {
    path: 'embedded/portfolio-detail',
    canActivate: [embeddedGuard],
    loadComponent: () =>
      import('./features/onboarding/portfolio-detail/portfolio-detail.component').then(
        (m) => m.PortfolioDetailComponent
      )
  },
  {
    path: 'public/portfolio-detail',
    resolve: { portfolio: publicPortfolioResolver },
    loadComponent: () =>
      import('./features/onboarding/portfolio-detail/portfolio-detail.component').then(
        (m) => m.PortfolioDetailComponent
      )
  },
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () =>
      import('./features/onboarding/start-process/start-process.component').then(
        (m) => m.StartProcessComponent
      )
  },
  {
    path: '**',
    redirectTo: ''
  }
];
